import debounce from "lodash/debounce";
import LoggedUserIndicator from "@molecules/logged-user-indicator/logged-user-indicator";
import { handleCustomUDL } from "@utils/UDLUtils";
import { isMainOrMiddleClick } from "@utils/MouseEventButton";
import { applyUTMParamsForLoginButtons } from "@utils/UTMParamUtils";
import { isDesktopViewport, isXlViewport } from "@utils/deviceCapabilities";
import identifyQualifiedTraffic from "@utils/identifyQualifiedTraffic";
import getLinkType from "./utils/getLinkType";
import { onClickOrAuxclick } from "@utils/onClickOrAuxclick";
import { EnhancedHoverNavigation } from "./EnhancedHoverNavigation";
import { EnhancedDropDownLinkList } from "./EnhancedDropDownLinkList";

// ─── Types ────────────────────────────────────────────────────────────────────

type NavigationType = "landing_navigation" | "microsite_navigation" | null;

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  active: "is-active",
  open: "is-open",
  branchLevel1: "enh-nav__branch--level-1",
  branchLevel2: "enh-nav__branch--level-2",
  level1Link: "enh-nav__link--primary",
  level2Link: "enh-nav__link--secondary",
  scrolledDown: "page-scrolled-down",
  scrolledAwayFromTop: "page-scrolled-away-from-top",
  siteAndLanguage: "comp-site-and-language-selector",
  isLandingNav: "enh-nav__landing-navigation",
  isMicrositeNav: "enh-nav__microsite-navigation",
  searchClass: "search",
  showHamburgerOnScroll: "hamburger-on-scroll",
};

const SELECTORS = {
  desktopTemplate: 'template[data-enh-nav-desktop-template]',
  initialDesktopHoverShell:
    '.enh-nav__hover-navigation[data-enh-nav-initial-shell="true"]',
  branchLevel1: `.${CLASSES.branchLevel1}`,
  branchLevel2: `.${CLASSES.branchLevel2}`,
  level1Link: `.${CLASSES.level1Link}`,
  level1LinkOnLevel2: ".enh-nav__link--primary-on-lvl2",
  level2Link: `.${CLASSES.level2Link}`,
  level3Link: ".enh-nav__link--tertiary",
  spotlightImage: ".enh-nav__spotlight-image",
  activeLevel1Link: `.${CLASSES.branchLevel1}.${CLASSES.active} .${CLASSES.level1Link}`,
  activeLevel2Link: `.${CLASSES.branchLevel2}.${CLASSES.active} .${CLASSES.level2Link}`,
  navBar: ".comp-nav-bar",
  breadcrumbs: ".comp-enhanced-breadcrumb",
  hoverNavigation: ".enh-nav__hover-navigation",
  metaNavigation: ".comp-enhanced-dropdown-linklist",
  quickLinks: ".enh-nav__quick-links > ul > li",
  siteSelector: `.${CLASSES.siteAndLanguage} .site-selector`,
  languageSelector: `.${CLASSES.siteAndLanguage} .language-selector > li`,
  ctaButton: ".comp-button",
  linksBarContent: ".enh-nav__links-bar-content",
  linksContainer: ".enh-nav__links-container",
  button: ".comp-button",
  header: ".enhanced-navigation",
  level2Wrapper: ".enh-nav__level-2-wrapper",
  accordionButtonContentContainer:
    ".enh-nav__accordion-button-content-container",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class EnhancedNavigation {
  private el: HTMLElement;
  private abortController: AbortController;
  private navigationType: NavigationType;
  private eventsBound = false;

  // Cached elements
  private navBar: Element | null = null;
  private breadcrumbs: Element | null = null;
  private hoverNavigation: HTMLElement | null = null;
  private hoverMetaNavigation: Element | null = null;
  private header: HTMLElement | null = null;
  private linksBarContent: HTMLElement | null = null;
  private linksContainer: HTMLElement | null = null;
  private linksBarButton: HTMLElement | null = null;

  // Links-bar widths (for microsite hamburger switch logic)
  private linksBarWidth = 0;
  private linksContainerWidth = 0;
  private buttonWidth = 0;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.navigationType = this.detectNavigationType();
  }

  init(): void {
    this.mountDesktopNavigationIfNeeded();
    this.bindElements();
    this.bindEvents();
    LoggedUserIndicator.updateLoginIndicator(this.el);
    this.setInitialLinksBarValues();

    if (this.navigationType === "microsite_navigation") {
      this.handleSwitchToHamburger();
    }

    applyUTMParamsForLoginButtons(this.el);
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Element binding ────────────────────────────────────────────────────────

  private bindElements(): void {
    this.navBar = this.el.closest(SELECTORS.navBar);
    this.breadcrumbs = document.querySelector(SELECTORS.breadcrumbs);
    this.hoverNavigation = this.el.querySelector<HTMLElement>(
      SELECTORS.hoverNavigation,
    );
    this.hoverMetaNavigation =
      this.hoverNavigation?.querySelector(SELECTORS.metaNavigation) ?? null;
    this.header = document.querySelector<HTMLElement>(SELECTORS.header);
    this.linksBarContent =
      this.hoverNavigation?.querySelector<HTMLElement>(
        SELECTORS.linksBarContent,
      ) ?? null;
    this.linksContainer =
      this.linksBarContent?.querySelector<HTMLElement>(
        SELECTORS.linksContainer,
      ) ?? null;
    this.linksBarButton =
      this.linksBarContent?.querySelector<HTMLElement>(SELECTORS.button) ??
      null;
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;
    this.eventsBound = true;

    this.handleScrollAnimations(signal);
    this.bindEventsOnRoot(this.el, signal);

    window.addEventListener(
      "resize",
      debounce(() => this.mountDesktopNavigationIfNeeded(), 150),
      { signal },
    );
  }

  private bindEventsOnRoot(root: ParentNode, signal: AbortSignal): void {
    root
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level1Link)
      .forEach((link) => {
        onClickOrAuxclick(link, (e) => this.clickLevel1Link(e, link), { signal });
      });

    root
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level1LinkOnLevel2)
      .forEach((link) => {
        onClickOrAuxclick(link, (e) => this.clickLevel1LinkMobile(e, link), { signal });
      });

    root
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level2Link)
      .forEach((link) => {
        onClickOrAuxclick(link, (e) => this.clickLevel2Link(e, link), { signal });
      });

    root
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.level3Link)
      .forEach((link) => {
        onClickOrAuxclick(link, (e) => this.clickLevel3Link(e, link), { signal });
      });

    root
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.spotlightImage)
      .forEach((link) => {
        onClickOrAuxclick(link, (e) => this.clickSpotlightLink(e, link), { signal });
      });

    this.handleQuickLinkUDL(root, signal);

    root.querySelectorAll(SELECTORS.siteSelector).forEach((el) => {
      onClickOrAuxclick(el, (e) => this.pushSiteSelectorDataToUDL(e), { signal });
    });

    root.querySelectorAll(SELECTORS.languageSelector).forEach((el) => {
      onClickOrAuxclick(el, (e) => this.pushLanguageSelectorDataToUDL(e), { signal });
    });

    root.querySelectorAll(SELECTORS.ctaButton).forEach((el) => {
      onClickOrAuxclick(el, (e) => this.clickCtaButton(e), { signal });
    });
  }

  private mountDesktopNavigationIfNeeded(): void {
    if (!isDesktopViewport()) return;

    const template = this.el.querySelector<HTMLTemplateElement>(
      SELECTORS.desktopTemplate,
    );
    if (!template) return;

    const initialDesktopHoverShell = this.el.querySelector<HTMLElement>(
      SELECTORS.initialDesktopHoverShell,
    );

    if (!initialDesktopHoverShell && this.el.querySelector(SELECTORS.hoverNavigation)) {
      return;
    }

    const fragment = template.content.cloneNode(true) as DocumentFragment;

    if (initialDesktopHoverShell) {
      initialDesktopHoverShell.replaceWith(fragment);
    } else {
      this.el.insertBefore(fragment, this.el.firstChild);
    }

    template.remove();

    this.bindElements();
    if (this.eventsBound) {
      this.bindEventsOnRoot(
        this.hoverNavigation ?? this.el,
        this.abortController.signal,
      );
    }
    this.initDynamicDesktopModules();
    this.setInitialLinksBarValues();

    if (this.navigationType === "microsite_navigation") {
      this.handleSwitchToHamburger();
    }
  }

  private initDynamicDesktopModules(): void {
    EnhancedHoverNavigation.initAll(this.el);
    EnhancedDropDownLinkList.initAll(this.el);
  }

  // ─── Navigation type detection ──────────────────────────────────────────────

  private detectNavigationType(): NavigationType {
    if (this.el.classList.contains(CLASSES.isLandingNav))
      return "landing_navigation";
    if (this.el.classList.contains(CLASSES.isMicrositeNav))
      return "microsite_navigation";
    return null;
  }

  // ─── Links-bar / hamburger switch ───────────────────────────────────────────

  private setInitialLinksBarValues(): void {
    this.linksBarWidth = this.linksBarContent?.offsetWidth ?? 0;
    this.linksContainerWidth = this.linksContainer?.offsetWidth ?? 0;
    this.buttonWidth = this.linksBarButton?.offsetWidth ?? 0;
  }

  private handleSwitchToHamburger(): void {
    if (this.linksBarWidth < this.linksContainerWidth + this.buttonWidth) {
      this.header?.classList.add(CLASSES.showHamburgerOnScroll);
    }
  }

  // ─── Scroll animations ──────────────────────────────────────────────────────

  private handleScrollAnimations(signal: AbortSignal): void {
    let prevScrollTop = 0;

    document.addEventListener(
      "scroll",
      () => {
        const currentScrollTop = window.scrollY;
        const hidingScrollValue = this.breadcrumbs ? 120 : 64;

        if (
          currentScrollTop > prevScrollTop &&
          currentScrollTop > hidingScrollValue
        ) {
          this.navBar?.classList.add(CLASSES.scrolledAwayFromTop);
          this.navBar?.classList.add(CLASSES.scrolledDown);
          this.hoverMetaNavigation?.classList.remove(CLASSES.open);
        } else {
          this.navBar?.classList.remove(CLASSES.scrolledDown);
          if (currentScrollTop <= hidingScrollValue) {
            this.navBar?.classList.remove(CLASSES.scrolledAwayFromTop);
          }
        }

        prevScrollTop = currentScrollTop;
      },
      { signal },
    );
  }

  // ─── Level-1 link handlers ──────────────────────────────────────────────────

  private clickLevel1Link(event: MouseEvent, link: HTMLAnchorElement): void {
    if (!isMainOrMiddleClick(event)) return;
    if (isXlViewport()) {
      this.clickLevel1LinkDesktop(link);
    } else {
      this.clickLevel1LinkMicroAndLandingMobile(link);
    }
  }

  private clickLevel1LinkDesktop(link: HTMLAnchorElement): void {
    const linkType = getLinkType(link);
    const link_url = link.href;

    if (this.navigationType === "landing_navigation") {
      this.pushLandingNavigationData({
        link_text: link.innerText,
        anchor_id: link.hash,
        navigation_level_1: "Landing",
        ...(linkType && { link_type: linkType }),
      });
      return;
    }

    if (this.navigationType === "microsite_navigation") {
      this.pushMicrositeNavigationData({
        link_text: link.innerText,
        link_url,
        navigation_level_1: "Microsite",
        ...(linkType && { link_type: linkType }),
        ...identifyQualifiedTraffic(link_url),
      });
      return;
    }

    this.pushData({
      link_url,
      navigation_level_1: link.innerText,
      navigation_level_2: undefined,
      navigation_level_3: undefined,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  private clickLevel1LinkMicroAndLandingMobile(
    link: HTMLAnchorElement,
  ): void {
    const linkType = getLinkType(link);
    const link_url = link.href;

    if (this.navigationType === "landing_navigation") {
      handleCustomUDL("hamburger_navigation", {
        link_text: link.innerText,
        anchor_id: link.hash,
        navigation_level_1: "Hamburger",
        menu_state: "collapsed_after_click",
        ...(linkType && { link_type: linkType }),
      });
      return;
    }

    if (this.navigationType === "microsite_navigation") {
      handleCustomUDL("hamburger_navigation", {
        link_text: link.innerText,
        link_url,
        navigation_level_1: "Hamburger",
        menu_state: "collapsed_after_click",
        ...(linkType && { link_type: linkType }),
        ...identifyQualifiedTraffic(link_url),
      });
    }
  }

  private clickLevel1LinkMobile(
    event: MouseEvent,
    link: HTMLAnchorElement,
  ): void {
    if (!isMainOrMiddleClick(event)) return;
    const link_url = link.href;
    this.pushData({
      link_url,
      navigation_level_1: link.innerText,
      navigation_level_2: undefined,
      navigation_level_3: undefined,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── Level-2 link handler ───────────────────────────────────────────────────

  private clickLevel2Link(event: MouseEvent, link: HTMLAnchorElement): void {
    if (!isMainOrMiddleClick(event)) return;
    const link_url = link.href;

    if (isXlViewport()) {
      const level1Active = this.el.querySelector<HTMLAnchorElement>(
        SELECTORS.activeLevel1Link,
      );
      this.pushData({
        link_url,
        navigation_level_1: level1Active?.innerText,
        navigation_level_2: link.innerText,
        navigation_level_3: undefined,
        ...identifyQualifiedTraffic(link_url),
      });
    } else {
      const level1Link = link
        .closest(SELECTORS.branchLevel1)
        ?.querySelector<HTMLAnchorElement>(SELECTORS.level1Link);
      this.pushData({
        link_url,
        navigation_level_1: level1Link?.innerText,
        navigation_level_2: link.innerText,
        navigation_level_3: undefined,
        ...identifyQualifiedTraffic(link_url),
      });
    }
  }

  // ─── Level-3 link handler ───────────────────────────────────────────────────

  private clickLevel3Link(event: MouseEvent, link: HTMLAnchorElement): void {
    if (!isMainOrMiddleClick(event)) return;
    const link_url = link.href;

    if (isXlViewport()) {
      const level1Active = this.el.querySelector<HTMLAnchorElement>(
        SELECTORS.activeLevel1Link,
      );
      const level2Active = this.el.querySelector<HTMLAnchorElement>(
        SELECTORS.activeLevel2Link,
      );
      this.pushData({
        link_url,
        navigation_level_1: level1Active?.innerText,
        navigation_level_2: level2Active?.innerText,
        navigation_level_3: link.innerText,
        ...identifyQualifiedTraffic(link_url),
      });
    } else {
      const level1Link = link
        .closest(SELECTORS.branchLevel1)
        ?.querySelector<HTMLAnchorElement>(SELECTORS.level1Link);
      const accordionBtn = link
        .closest(SELECTORS.level2Wrapper)
        ?.querySelector<HTMLElement>(SELECTORS.accordionButtonContentContainer);
      this.pushData({
        link_url,
        navigation_level_1: level1Link?.innerText,
        navigation_level_2: accordionBtn?.innerText,
        navigation_level_3: link.innerText,
        ...identifyQualifiedTraffic(link_url),
      });
    }
  }

  // ─── Spotlight link handler ─────────────────────────────────────────────────

  private clickSpotlightLink(
    event: MouseEvent,
    link: HTMLAnchorElement,
  ): void {
    if (!isMainOrMiddleClick(event)) return;
    const link_url = link.href;
    this.pushDataSpotlight({
      link_url,
      link_text: link.innerText,
      ...identifyQualifiedTraffic(link_url),
    });
  }

  // ─── CTA button handler ─────────────────────────────────────────────────────

  private clickCtaButton(event: MouseEvent): void {
    const btn = event.currentTarget as HTMLElement;
    const text = btn.textContent?.trim() ?? "";
     const anchor = btn.querySelector<HTMLAnchorElement>("a");
     const href = anchor?.getAttribute("href") ?? undefined;
     const hash = anchor?.hash || undefined;

    if (this.navigationType === "microsite_navigation") {
      handleCustomUDL("cta_click", {
        cta_text: text,
        cta_url: href,
        cta_position: "header",
      });
      return;
    }

    if (this.navigationType === "landing_navigation") {
      handleCustomUDL("cta_click_landing", {
        cta_text: text,
        cta_url: hash ?? href,
        cta_position: "landing header",
      });
    }
  }

  // ─── Quick-links UDL ────────────────────────────────────────────────────────

  private handleQuickLinkUDL(root: ParentNode, signal: AbortSignal): void {
    root.querySelectorAll(SELECTORS.quickLinks).forEach((linkEl) => {
      const handler = (event: MouseEvent): void => {
        if (!isMainOrMiddleClick(event)) return;

        const linkName = linkEl.querySelector("a")?.getAttribute("aria-label");

        if (this.navigationType === "microsite_navigation") {
          const href = linkEl.querySelector("a")?.getAttribute("href");
          handleCustomUDL("service_icon_click", {
            icon_name: linkName,
            action_type: "click",
            destination: href,
          });
          return;
        }

        if (this.navigationType === "landing_navigation") {
          handleCustomUDL("service_icon_click_landing", {
            icon_name: linkName,
            action_type: "click",
            component_context: "Landing Header",
          });
          return;
        }

        if (!linkEl.classList.contains(CLASSES.searchClass)) {
          handleCustomUDL("service_navigation", { service: linkName });
        }
      };

      onClickOrAuxclick(linkEl, handler, { signal });
    });
  }

  // ─── UDL push helpers ───────────────────────────────────────────────────────

  private pushData(params: Record<string, unknown>): void {
    handleCustomUDL("top_navigation", params);
  }

  private pushDataSpotlight(params: Record<string, unknown>): void {
    handleCustomUDL("top_navigation_spotlight_click", params);
  }

  private pushLandingNavigationData(params: Record<string, unknown>): void {
    handleCustomUDL("landing_navigation", params);
  }

  private pushMicrositeNavigationData(params: Record<string, unknown>): void {
    handleCustomUDL("microsite_navigation", params);
  }

  private pushSiteSelectorDataToUDL(event: MouseEvent): void {
    if (!isMainOrMiddleClick(event)) return;
    const country = (event.currentTarget as HTMLElement).textContent?.trim();
    handleCustomUDL("country_selector", { country });
  }

  private pushLanguageSelectorDataToUDL(event: MouseEvent): void {
    if (!isMainOrMiddleClick(event)) return;
    const language = (event.currentTarget as HTMLElement).textContent?.trim();
    handleCustomUDL("language_selector", { language });
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="enhanced-navigation"]')
      .forEach((el) => {
        new EnhancedNavigation(el).init();
      });
  }
}
